/**
 * @module useUpperFunnelToaster
 * @description Custom hook for managing upper funnel toaster state with auto-expiring localStorage persistence
 */

import { useEffect, useState } from 'react';

/**
 * @function useUpperFunnelToaster
 * @description Hook that provides state management for upper funnel toaster with auto-expiring localStorage persistence
 *
 * @param {number} storeTimerValue - Time in milliseconds after which the localStorage value will be removed
 * @param {string} [key='upper-funnel-switch'] - The localStorage key to store the toaster state
 * @param {boolean} [initialValue=true] - Default value if nothing is found in localStorage
 * @returns {Array} - A stateful value and a function to update it:
 *   [0] {boolean} storedValue - The current toaster state (from localStorage or initialValue)
 *   [1] {Function} setValue - Function to update the toaster state
 *
 * @example
 * const [showToaster, setShowToaster] = useUpperFunnelToaster(3600000); // 1 hour expiry
 *
 * // Later in your component:
 * return (
 *   <>
 *     {showToaster && <UpperFunnelToaster onClose={() => setShowToaster(false)} />}
 *     <button onClick={() => setShowToaster(true)}>Show Toaster</button>
 *   </>
 * );
 */
export default function useUpperFunnelToaster(storeTimerValue, key = 'upper-funnel-switch', initialValue = true) {
  let currentStoredValue = null;

  if (typeof window !== 'undefined') {
    currentStoredValue = JSON.parse(localStorage.getItem(key) || JSON.stringify(initialValue));
  }

  const [storedValue, setStoredValue] = useState(currentStoredValue);

  useEffect(() => {
    /**
     * @function removeLocalStorageValue
     * @description Removes the value from localStorage and resets state to initial value
     * @private
     */
    const removeLocalStorageValue = () => {
      localStorage.removeItem(key);
      setStoredValue(initialValue);
    };

    const interval = setInterval(removeLocalStorageValue, storeTimerValue);

    return () => { clearInterval(interval); };
  }, [initialValue, key, storeTimerValue]);

  /**
   * @function setValue
   * @description Updates both the React state and localStorage
   * @param {boolean} value - New toaster state value
   */
  const setValue = (value) => {
    setStoredValue(value);
    localStorage.setItem(key, JSON.stringify(value));
  };

  return [storedValue, setValue];
}
